import { defineStore } from 'pinia';
import consts from '@/consts';
import Vue from 'vue';
import iso from 'iso-3166-1';

export const PointsStore = defineStore('points', {
    state: () => ({
        devices: [],
        device_points: {},       // { slug: points[] }
        device_visibility: {},   // { slug: true/false } - for map
        selected_device: null,   // slug of device selected for graphs/table
        gates: [],
        focused_point: null,
        loading: true,
        all_gates: [],
        device_countries: [],
        gateways_countries: []
    }),
    getters: {
        points() {
            if (!this.selected_device || !this.device_points[this.selected_device]) {
                return [];
            }
            return this.device_points[this.selected_device];
        },
        visible_devices() {
            return this.devices.filter(d => this.device_visibility[d.slug]);
        },
        all_visible_points() {
            const result = [];
            for (const device of this.devices) {
                if (this.device_visibility[device.slug] && this.device_points[device.slug]) {
                    const index = this.devices.findIndex(d => d.slug === device.slug);
                    const color = consts.DEVICE_COLORS[index % consts.DEVICE_COLORS.length];
                    for (const point of this.device_points[device.slug]) {
                        result.push({ ...point, device_slug: device.slug, device_color: color, device_name: device.name });
                    }
                }
            }
            return result;
        }
    },
    actions: {
        get_device_color(slug) {
            const index = this.devices.findIndex(d => d.slug === slug);
            return consts.DEVICE_COLORS[index % consts.DEVICE_COLORS.length];
        },
        is_device_debug(slug) {
            const device = this.devices.find(d => d.slug === slug);
            return device ? device.released_after_id === null : true;
        },
        has_any_debug_device() {
            return this.devices.some(d => d.released_after_id === null);
        },
        async fetch_devices() {
            try {
                const response = await Vue.axios.get(`${consts.SERVER_URL}/devices`);
                this.devices = response.data;
                const visibility = { ...this.device_visibility };
                for (const device of this.devices) {
                    if (visibility[device.slug] === undefined) {
                        visibility[device.slug] = true;
                    }
                }
                this.device_visibility = visibility;
                if (this.devices.length > 0 && !this.selected_device) {
                    this.selected_device = this.devices[0].slug;
                }
            } catch (err) {
                console.log(err);
            }
        },
        async fetch_device_points(slug) {
            try {
                const response = await Vue.axios.get(`${consts.SERVER_URL}/${slug}/data`);
                let points = (response.data.sort((a, b) => parseInt(a.id) - parseInt(b.id))).reverse();
                points = points.map(point => this.parse_point(point));
                points = points.filter((item, idx, self) => idx === self.findIndex(t => t.time === item.time));
                this.device_points = { ...this.device_points, [slug]: points };
            } catch (err) {
                console.log(err);
            }
        },
        async fetch_all_device_points() {
            this.loading = true;
            const results = {};
            await Promise.all(this.devices.map(async (device) => {
                try {
                    const response = await Vue.axios.get(`${consts.SERVER_URL}/${device.slug}/data`);
                    let points = (response.data.sort((a, b) => parseInt(a.id) - parseInt(b.id))).reverse();
                    points = points.map(point => this.parse_point(point));
                    points = points.filter((item, idx, self) => idx === self.findIndex(t => t.time === item.time));
                    results[device.slug] = points;
                } catch (err) {
                    console.log(err);
                    results[device.slug] = [];
                }
            }));
            this.device_points = { ...this.device_points, ...results };
            this.loading = false;
            if (this.selected_device && this.device_points[this.selected_device]?.length > 0) {
                this.update_focused_point(this.device_points[this.selected_device][0]);
            }
        },
        async get_last_point_for_device(slug) {
            try {
                const response = await Vue.axios.get(`${consts.SERVER_URL}/${slug}/last-data`);
                if (response.data.length === 0) return;
                let point = this.parse_point(response.data[0]);
                let device_pts = this.device_points[slug] || [];
                if (device_pts.length === 0 || device_pts[0].time !== point.time) {
                    device_pts.unshift(point);
                    this.device_points[slug] = [...device_pts];
                    if (slug === this.selected_device) {
                        this.update_focused_point(point);
                    }
                    return true;
                }
                return false;
            } catch (err) {
                console.log(err);
                return false;
            }
        },
        async get_last_points() {
            const results = await Promise.all(
                this.devices.map(device => this.get_last_point_for_device(device.slug))
            );
            return results.some(r => r === true);
        },
        async fetch_device_countries() {
            try {
                const response = await Vue.axios.get(`${consts.SERVER_URL}/device-countries`);
                this.device_countries = response.data; // e.g. ["cz", "sk", "at"]
            } catch (err) {
                console.log(err);
                this.device_countries = [];
            }
        },
        async get_lora_ttn_gates() {
            await Vue.axios.get(consts.LORA_TTN_GATES)
                .then((response) => {
                    let cookie = Vue.$cookies.get(consts.COUNTRIES_COOKIE);

                    // Merge: device countries (auto) + cookie (user selection)
                    let device_country_objects = this.device_countries
                        .map(code => iso.whereAlpha2(code))
                        .filter(c => c);

                    let selected_countries;
                    if (cookie !== null && cookie.countries !== undefined) {
                        // Merge cookie selection with device countries
                        let cookie_codes = cookie.countries.map(c => c.alpha2);
                        let merged_codes = new Set([...cookie_codes, ...this.device_countries.map(c => c.toUpperCase())]);
                        selected_countries = [...merged_codes]
                            .map(code => iso.whereAlpha2(code))
                            .filter(c => c);
                    } else {
                        selected_countries = device_country_objects;
                    }

                    this.gateways_countries = selected_countries;
                    Vue.$cookies.set(consts.COUNTRIES_COOKIE, {countries: selected_countries});

                    this.all_gates = response.data;
                    this.gates = this.filter_gates(selected_countries.map(country => country.alpha2.toLowerCase()));

                }).catch((err) => {
                    console.log(err);
            })
        },
        parse_point(point) {
          let date_time = point.time.split(' ');
          let date_format = date_time[0].split('.').reverse().join('-');
          point.time = `${date_format} ${date_time[1]}`;
          point.altitude = point.altitude != null ? Number(point.altitude).toFixed(2) : null;
          point.temperature = point.temperature != null ? Number(point.temperature).toFixed(3) : null;
          point.mcu_temperature = point.mcu_temperature != null ? Number(point.mcu_temperature).toFixed(3) : null;
          point.humidity = point.humidity != null ? Number(point.humidity).toFixed(2) : null;
          point.gas_resistance = point.gas_resistance != null ? Number(point.gas_resistance).toFixed(0) : null;
          return point;
        },
        update_focused_point(point) {
            this.focused_point = point;
        },
        toggle_device_visibility(slug) {
            this.device_visibility[slug] = !this.device_visibility[slug];
            this.device_visibility = { ...this.device_visibility };
        },
        select_device(slug) {
            this.selected_device = slug;
            if (this.device_points[slug]?.length > 0) {
                this.update_focused_point(this.device_points[slug][0]);
            }
        },
        filter_gates(selected_countries) {
          return Object.values(this.all_gates).filter(
            gateway => {
              if ('attributes' in gateway) {
                return selected_countries.includes(gateway.country_code);
              } else {
                return false;
              }
            }
          );
        }
    }
});
